import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { useToast } from '@/hooks/use-toast';
import SectionShortcutsLauncher from '@/components/SectionShortcutsLauncher';
import type { Database } from '@/integrations/supabase/types';
import { getHouseholdSharePeerName } from '@/lib/household-share';
import {
  AppButton,
  AppCard,
  AppEmptyState,
  AppPage,
  AppPageIntro,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import {
  buildPetDocumentsSummary,
  buildPetFoodSignal,
  buildPetFoodStatus,
  buildPetFoodSummary,
  buildPetGuidanceSummary,
  buildPetHubNextStep,
  buildPetHubSignals,
  buildPetMissingRecordPrompts,
  buildPetProfileSummary,
  buildPetSharedCareStatus,
  buildPetSpendSummary,
  hasTrackedPets,
} from '../../ui-foundation/features/pets';
import PetFoodForm from '@/components/pets/PetFoodForm';

type PetRow = Pick<
  Database['public']['Tables']['pets']['Row'],
  | 'id'
  | 'name'
  | 'pet_type'
  | 'breed'
  | 'dob'
  | 'household_share_id'
  | 'age_notes'
  | 'microchip_number'
  | 'adoption_date'
  | 'weight_kg'
  | 'health_conditions_summary'
  | 'insurance_summary'
  | 'user_id'
  | 'vet_practice_summary'
>;

type HealthReminderRow = Pick<
  Database['public']['Tables']['health_reminders']['Row'],
  'id' | 'subject_id' | 'subject_type' | 'due_date' | 'completed_at'
>;

type HealthMedicationRow = Pick<
  Database['public']['Tables']['health_medications']['Row'],
  'id' | 'subject_id' | 'subject_type'
>;

type PetFoodRow = {
  id: string;
  pet_id: string;
  brand_name: string;
  flavour: string | null;
  package_size: string | null;
  price: number | null;
  last_purchase_date: string;
  lasts_for_days: number;
  refill_reminder_days_before: number;
  notes: string | null;
};

type PetDocumentRow = {
  id: string;
  file_name: string;
  folder: string;
  uploaded_at: string;
};

const TODAY_ISO = new Date().toISOString().slice(0, 10);

export default function PetsPage() {
  const navigate = useNavigate();
  const { user, data } = useApp();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pets, setPets] = useState<PetRow[]>([]);
  const [healthReminders, setHealthReminders] = useState<HealthReminderRow[]>([]);
  const [healthMedications, setHealthMedications] = useState<HealthMedicationRow[]>([]);
  const [petFoods, setPetFoods] = useState<PetFoodRow[]>([]);
  const [petDocuments, setPetDocuments] = useState<PetDocumentRow[]>([]);
  const [editingPetFoodPetId, setEditingPetFoodPetId] = useState<string | null>(null);
  const [sharingPetId, setSharingPetId] = useState<string | null>(null);

  const loadPetsHub = useCallback(async () => {
    if (!user) {
      setPets([]);
      setHealthReminders([]);
      setHealthMedications([]);
      setErrorMessage(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const [petsResult, remindersResult, medicationsResult, petFoodsResult, petDocumentsResult] = await Promise.all([
      supabase
        .from('pets')
        .select('id,user_id,name,pet_type,breed,dob,household_share_id,age_notes,microchip_number,adoption_date,weight_kg,health_conditions_summary,insurance_summary,vet_practice_summary')
        .order('name'),
      supabase
        .from('health_reminders')
        .select('id,subject_id,subject_type,due_date,completed_at')
        .eq('user_id', user.id)
        .eq('subject_type', 'pet'),
      supabase
        .from('health_medications')
        .select('id,subject_id,subject_type')
        .eq('user_id', user.id)
        .eq('subject_type', 'pet'),
      (supabase as any)
        .from('pet_food_profiles')
        .select('*')
        .order('created_at', { ascending: false }),
      supabase
        .from('documents')
        .select('id,file_name,folder,uploaded_at')
        .eq('user_id', user.id)
        .eq('folder', 'pets')
        .order('uploaded_at', { ascending: false }),
    ]);

    const firstError = petsResult.error || remindersResult.error || medicationsResult.error || petFoodsResult.error || petDocumentsResult.error;
    if (firstError) {
      setErrorMessage(firstError.message);
      toast({
        title: 'Could not refresh My Pets',
        description: firstError.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    setPets((petsResult.data || []) as PetRow[]);
    setHealthReminders((remindersResult.data || []) as HealthReminderRow[]);
    setHealthMedications((medicationsResult.data || []) as HealthMedicationRow[]);
    setPetFoods((petFoodsResult.data || []) as PetFoodRow[]);
    setPetDocuments((petDocumentsResult.data || []) as PetDocumentRow[]);
    setErrorMessage(null);
    setLoading(false);
  }, [toast, user]);

  useEffect(() => {
    void loadPetsHub();
  }, [loadPetsHub]);

  useEffect(() => {
    const handleRefresh = () => {
      void loadPetsHub();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [loadPetsHub]);

  const trackedPets = useMemo(() => pets.filter((pet) => pet.name?.trim().length), [pets]);
  const hasPets = hasTrackedPets(trackedPets);
  const householdPeerName = useMemo(
    () => getHouseholdSharePeerName(data.householdShare, user?.id || null),
    [data.householdShare, user?.id],
  );
  const hasSharedPets = useMemo(
    () => trackedPets.some((pet) => Boolean(data.householdShare?.id && pet.household_share_id === data.householdShare.id)),
    [data.householdShare?.id, trackedPets],
  );

  const remindersByPetId = useMemo(() => {
    const map = new Map<string, HealthReminderRow[]>();
    healthReminders.forEach((row) => {
      if (!row.subject_id) return;
      const current = map.get(row.subject_id) || [];
      current.push(row);
      map.set(row.subject_id, current);
    });
    return map;
  }, [healthReminders]);

  const medicationsByPetId = useMemo(() => {
    const map = new Map<string, HealthMedicationRow[]>();
    healthMedications.forEach((row) => {
      if (!row.subject_id) return;
      const current = map.get(row.subject_id) || [];
      current.push(row);
      map.set(row.subject_id, current);
    });
    return map;
  }, [healthMedications]);

  const foodsByPetId = useMemo(() => {
    const map = new Map<string, PetFoodRow>();
    petFoods.forEach((row) => {
      map.set(row.pet_id, row);
    });
    return map;
  }, [petFoods]);

  const petDocumentsSummary = useMemo(
    () =>
      buildPetDocumentsSummary({
        documents: petDocuments.map((document) => ({
          folder: document.folder,
          fileName: document.file_name,
          uploadedAt: document.uploaded_at,
        })),
      }),
    [petDocuments],
  );

  const petSpendSummary = useMemo(
    () =>
      buildPetSpendSummary({
        expenses: data.expenses,
      }),
    [data.expenses],
  );

  const togglePetSharedCare = useCallback(async (pet: PetRow) => {
    if (!user) return;
    if (!data.householdShare?.id) {
      navigate('/profile');
      return;
    }

    setSharingPetId(pet.id);
    const nextHouseholdShareId = pet.household_share_id === data.householdShare.id ? null : data.householdShare.id;

    const { error } = await supabase
      .from('pets')
      .update({ household_share_id: nextHouseholdShareId })
      .eq('id', pet.id);

    setSharingPetId(null);

    if (error) {
      toast({
        title: 'Could not update shared care',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: nextHouseholdShareId ? 'Shared care turned on' : 'Shared care turned off',
      description: nextHouseholdShareId
        ? `${pet.name} now shows up for ${householdPeerName || 'your linked household'} too.`
        : `${pet.name} is back to your account only.`,
    });
    window.dispatchEvent(new CustomEvent('adulting:data-changed'));
    await loadPetsHub();
  }, [data.householdShare?.id, householdPeerName, loadPetsHub, navigate, toast, user]);

  const openSinglePetFoodShortcut = useCallback(() => {
    const firstTrackedPet = trackedPets[0];
    if (!firstTrackedPet) {
      return;
    }

    setEditingPetFoodPetId(firstTrackedPet.id);
  }, [trackedPets]);

  return (
    <AppPage className="animate-fade-in">
      <AppPageIntro
        title="My Pets"
        subtitle="Keep the pet basics, care reminders, and the main next step in one calm place without turning Adulting into a separate pet app."
        action={hasPets ? (
          <SectionShortcutsLauncher
            title="Pet shortcuts"
            subtitle="Open the next useful pet action without leaving a permanent row of buttons on screen."
            items={[
              {
                id: 'pet-food',
                label: 'Track pet food',
                description: 'Open the current food profile for your saved pet.',
                onPress: openSinglePetFoodShortcut,
                hidden: trackedPets.length !== 1,
              },
              {
                id: 'health',
                label: 'Open My Health',
                description: 'Jump to reminders, medications, and pet care history.',
                onPress: () => navigate('/health'),
              },
              {
                id: 'documents',
                label: 'Open Documents',
                description: 'Go straight to pet paperwork in the document vault.',
                onPress: () => navigate('/documents'),
              },
              {
                id: 'settings',
                label: 'Manage in My Settings',
                description: 'Update pet records and profile basics.',
                onPress: () => navigate('/profile'),
              },
            ]}
          />
        ) : undefined}
      />

      {errorMessage ? (
        <AppCard>
          <AppSectionHeader title="My Pets could not refresh just now" subtitle={errorMessage} />
          <AppButton tone="outline" size="sm" onPress={() => void loadPetsHub()}>
            Try again
          </AppButton>
        </AppCard>
      ) : null}

      {loading && !hasPets ? (
        <AppCard>
          <AppSectionHeader title="Loading My Pets" subtitle="Pulling your saved pet records and linked health items now." />
        </AppCard>
      ) : null}

      {!loading && !hasPets ? (
        <AppEmptyState
          title="No pet profiles saved yet"
          body="Add a pet in My Settings and Adulting will bring My Pets into More once there is something real to organise."
          action={(
            <AppButton onPress={() => navigate('/profile')}>
              Open My Settings
            </AppButton>
          )}
        />
      ) : null}

      {hasPets ? (
        <>
          <AppCard>
            <AppSectionHeader
              title="Pet overview"
              subtitle={`${trackedPets.length} pet${trackedPets.length === 1 ? '' : 's'} saved. ${healthReminders.length} pet reminder${healthReminders.length === 1 ? '' : 's'}, ${healthMedications.length} medication${healthMedications.length === 1 ? '' : 's'}, ${petFoods.length} food profile${petFoods.length === 1 ? '' : 's'}, and ${petDocumentsSummary.count} pet document${petDocumentsSummary.count === 1 ? '' : 's'} are already linked.`}
            />
            <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
              Use shortcuts for quick jumps, while Health, Documents, Money, and My Settings still own the underlying pet records.
            </AppText>
          </AppCard>

          <AppCard>
            <AppSectionHeader
              title="Paperwork and money"
              subtitle="Keep pet paperwork visible from here, while Documents and Money stay the sections that own the actual records."
            />
            <div className="mt-3 space-y-3">
              <div>
                <AppText fontSize="$sm" fontWeight="700">Documents</AppText>
                <AppText fontSize="$xs" color="$colorMuted">
                  {petDocumentsSummary.hasDocuments
                    ? `${petDocumentsSummary.count} pet document${petDocumentsSummary.count === 1 ? '' : 's'} saved. Latest: ${petDocumentsSummary.latestFileName || 'recent pet paperwork'}.`
                    : 'No pet paperwork saved yet. Insurance, microchip, adoption, and vet documents can stay in the Pets folder in Documents.'}
                </AppText>
              </div>
              <div>
                <AppText fontSize="$sm" fontWeight="700">Money</AppText>
                <AppText fontSize="$xs" color="$colorMuted">
                  {petSpendSummary.hasAnySpend
                    ? `${petSpendSummary.expenseCount} pet expense${petSpendSummary.expenseCount === 1 ? '' : 's'} saved in My Money. ${petSpendSummary.currentMonthExpenseCount ? `This month: £${petSpendSummary.currentMonthTotal.toFixed(2)}.` : 'No pet spend logged this month yet.'} ${petSpendSummary.recurringCount ? `${petSpendSummary.recurringCount} recurring pet cost${petSpendSummary.recurringCount === 1 ? '' : 's'} already visible.` : 'Recurring pet costs will stay visible there once they are saved or recognised.'}`
                    : 'No pet spend is visible yet. Once pet costs are logged or recognised, Adulting will keep them visible in My Money.'}
                </AppText>
              </div>
              {hasSharedPets ? (
                <div>
                  <AppText fontSize="$sm" fontWeight="700">Shared-care note</AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Shared pet records can show up for both adults, but paperwork and spend still stay with the adult account that saved them.
                  </AppText>
                </div>
              ) : null}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <AppButton size="sm" tone="outline" onPress={() => navigate('/documents')}>
                Open Documents
              </AppButton>
              <AppButton size="sm" tone="outline" onPress={() => navigate('/money')}>
                Open My Money
              </AppButton>
            </div>
          </AppCard>

          <div className="space-y-4">
            {trackedPets.map((pet) => {
              const reminderRows = remindersByPetId.get(pet.id) || [];
              const medicationRows = medicationsByPetId.get(pet.id) || [];
              const foodRow = foodsByPetId.get(pet.id) || null;
              const dueReminderCount = reminderRows.filter((row) => !row.completed_at && row.due_date && row.due_date <= TODAY_ISO).length;
              const summaryParts = buildPetProfileSummary({
                petType: pet.pet_type,
                breed: pet.breed,
                dateOfBirth: pet.dob,
                ageNotes: pet.age_notes,
                microchipNumber: pet.microchip_number,
                adoptionDate: pet.adoption_date,
                weightKg: pet.weight_kg,
                healthConditionsSummary: pet.health_conditions_summary,
                insuranceSummary: pet.insurance_summary,
                vetPracticeSummary: pet.vet_practice_summary,
              });
              const hubSignals = buildPetHubSignals({
                reminderCount: reminderRows.length,
                dueReminderCount,
                medicationCount: medicationRows.length,
              });
              const foodSignal = buildPetFoodSignal(foodRow);
              const foodSummary = buildPetFoodSummary(foodRow);
              const foodStatus = buildPetFoodStatus(foodRow);
              const nextStep = buildPetHubNextStep({
                reminderCount: reminderRows.length,
                dueReminderCount,
                medicationCount: medicationRows.length,
                healthConditionsSummary: pet.health_conditions_summary,
                insuranceSummary: pet.insurance_summary,
                vetPracticeSummary: pet.vet_practice_summary,
              });
              const isSharedWithHousehold = Boolean(data.householdShare?.id && pet.household_share_id === data.householdShare.id);
              const canManageSharedCare = pet.user_id === user?.id;
              const sharedCareStatus = buildPetSharedCareStatus({
                petName: pet.name,
                peerName: householdPeerName,
                hasHouseholdShare: Boolean(data.householdShare?.id),
                isSharedWithHousehold,
              });
              const missingPrompts = buildPetMissingRecordPrompts({
                petName: pet.name,
                peerName: householdPeerName,
                hasHouseholdShare: Boolean(data.householdShare?.id),
                isSharedWithHousehold,
                reminderCount: reminderRows.length,
                dueReminderCount,
                medicationCount: medicationRows.length,
                hasFoodProfile: Boolean(foodRow),
                hasMicrochipNumber: Boolean(pet.microchip_number),
                hasInsuranceSummary: Boolean(pet.insurance_summary),
                hasVetPracticeSummary: Boolean(pet.vet_practice_summary),
                hasDocuments: petDocumentsSummary.hasDocuments,
                canPromptForDocuments: pet.user_id === user?.id,
              });
              const guidanceSummary = buildPetGuidanceSummary({
                petName: pet.name,
                peerName: householdPeerName,
                hasHouseholdShare: Boolean(data.householdShare?.id),
                isSharedWithHousehold,
                reminderCount: reminderRows.length,
                dueReminderCount,
                medicationCount: medicationRows.length,
                hasFoodProfile: Boolean(foodRow),
                hasMicrochipNumber: Boolean(pet.microchip_number),
                hasInsuranceSummary: Boolean(pet.insurance_summary),
                hasVetPracticeSummary: Boolean(pet.vet_practice_summary),
                hasDocuments: petDocumentsSummary.hasDocuments,
                canPromptForDocuments: pet.user_id === user?.id,
              });

              return (
                <AppCard key={pet.id}>
                  <AppText fontSize="$lg" fontWeight="700">{pet.name}</AppText>
                  <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                    {summaryParts.length ? summaryParts.slice(0, 5).join(' · ') : 'Saved in My Settings and ready for reminders.'}
                  </AppText>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hubSignals.map((signal) => (
                      <div
                        key={`${pet.id}-${signal}`}
                        className="rounded-full border border-border bg-muted/60 px-3 py-1 text-[11px] font-semibold text-muted-foreground"
                      >
                        {signal}
                      </div>
                    ))}
                    <div className="rounded-full border border-border bg-muted/60 px-3 py-1 text-[11px] font-semibold text-muted-foreground">
                      {foodSignal}
                    </div>
                  </div>
                  <div className="mt-4 rounded-2xl border border-border bg-background px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <AppText fontSize="$sm" fontWeight="700">Food and refill timing</AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {foodSummary.length
                            ? foodSummary.join(' · ')
                            : 'Add the current food once and Adulting will keep the likely runout and refill window visible here.'}
                        </AppText>
                      </div>
                      <div
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                          foodStatus.tone === 'danger'
                            ? 'bg-red-100 text-red-800'
                            : foodStatus.tone === 'warning'
                              ? 'bg-amber-100 text-amber-800'
                              : foodStatus.tone === 'positive'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {foodStatus.headline}
                      </div>
                    </div>
                    <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                      {foodStatus.detail}
                    </AppText>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <AppButton size="sm" tone="outline" onPress={() => setEditingPetFoodPetId(pet.id)}>
                        {foodRow ? 'Edit food profile' : 'Add food profile'}
                      </AppButton>
                    </div>
                  </div>
                  <div className="mt-4 rounded-2xl border border-border bg-background px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <AppText fontSize="$sm" fontWeight="700">Shared care</AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {sharedCareStatus.body}
                        </AppText>
                      </div>
                      <div className="rounded-full bg-muted px-3 py-1 text-[11px] font-semibold text-muted-foreground">
                        {sharedCareStatus.headline}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {data.householdShare?.id && canManageSharedCare ? (
                        <AppButton
                          size="sm"
                          tone="outline"
                          disabled={sharingPetId === pet.id}
                          onPress={() => void togglePetSharedCare(pet)}
                        >
                          {sharingPetId === pet.id
                            ? 'Updating…'
                            : isSharedWithHousehold
                              ? 'Keep in my account only'
                              : `Share with ${householdPeerName || 'household'}`}
                        </AppButton>
                      ) : data.householdShare?.id ? (
                        <AppText fontSize="$xs" color="$colorMuted">
                          Shared-care visibility can only be changed from the adult account that created this pet.
                        </AppText>
                      ) : (
                        <AppButton size="sm" tone="outline" onPress={() => navigate('/profile')}>
                          Link household in My Settings
                        </AppButton>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 rounded-2xl border border-border bg-background px-4 py-4">
                    <AppText fontSize="$sm" fontWeight="700">Adulting summary</AppText>
                    <AppText className="mt-1" fontSize="$sm" fontWeight="600">
                      {guidanceSummary.headline}
                    </AppText>
                    <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                      {guidanceSummary.body}
                    </AppText>
                    {missingPrompts.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {missingPrompts.slice(0, 3).map((prompt) => (
                          <div key={`${pet.id}-${prompt.key}`}>
                            <AppText fontSize="$xs" fontWeight="700">{prompt.title}</AppText>
                            <AppText fontSize="$xs" color="$colorMuted">{prompt.body}</AppText>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
                        {nextStep}
                      </AppText>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <AppButton size="sm" tone="outline" onPress={() => navigate('/health')}>
                      Open My Health
                    </AppButton>
                    <AppButton size="sm" tone="outline" onPress={() => navigate('/profile')}>
                      Edit in My Settings
                    </AppButton>
                  </div>
                </AppCard>
              );
            })}
          </div>

          <AppCard>
            <AppSectionHeader
              title="Where pet things live"
              subtitle="My Pets gives you one calm place to orient yourself, but the actual work still stays with the section that already owns it."
            />
            <div className="space-y-3">
              <div>
                <AppText fontSize="$sm" fontWeight="700">My Settings</AppText>
                <AppText fontSize="$xs" color="$colorMuted">Pet records, microchip, vet, insurance, and profile basics.</AppText>
              </div>
              <div>
                <AppText fontSize="$sm" fontWeight="700">My Health</AppText>
                <AppText fontSize="$xs" color="$colorMuted">Appointments, medications, pet care history, and recurring care reminders stay in the health flow.</AppText>
              </div>
              <div>
                <AppText fontSize="$sm" fontWeight="700">Documents</AppText>
                <AppText fontSize="$xs" color="$colorMuted">Insurance, microchip, adoption, and vet paperwork stay in the Pets folder inside Documents.</AppText>
              </div>
              <div>
                <AppText fontSize="$sm" fontWeight="700">My Pets</AppText>
                <AppText fontSize="$xs" color="$colorMuted">Current food profile, paperwork visibility, and the main next step live here.</AppText>
              </div>
              <div>
                <AppText fontSize="$sm" fontWeight="700">My Money</AppText>
                <AppText fontSize="$xs" color="$colorMuted">Pet spending and recurring pet costs stay visible in the finance view there.</AppText>
              </div>
            </div>
          </AppCard>
        </>
      ) : null}

      {editingPetFoodPetId ? (
        <PetFoodForm
          petId={editingPetFoodPetId}
          petName={trackedPets.find((pet) => pet.id === editingPetFoodPetId)?.name || 'this pet'}
          existingFood={foodsByPetId.get(editingPetFoodPetId) || null}
          onClose={() => setEditingPetFoodPetId(null)}
          onSaved={() => {
            setEditingPetFoodPetId(null);
            void loadPetsHub();
          }}
        />
      ) : null}
    </AppPage>
  );
}
